/**
 * CinematicPlayer — executes a TimelineData against a CinematicHost.
 *
 * Phaser-free so the skip/convergence logic is unit-testable. The host adapts the ops to a scene
 * (see SceneHost.ts). Missing host capabilities degrade to a warning, never a hang.
 *
 * SKIP semantics: `skip()` (Esc / ctx.skipHandler / input:cancel) fast-forwards the rest of the
 * timeline. Waits, tweens, emotes, fx and sfx are cut; state-affecting ops still apply — setFlag,
 * setMark, weather (instant), music, final actor positions/facing/anim, camera end state, fade end
 * state, silent dialogue effects, memory-stage state replay. Play and skip therefore end in the same
 * story state. A `battle` step is gameplay, not a cutscene: a player skip still plays it (the steps
 * after it stay in skip mode); only `play(tl, { fastForward: true })` (state-only replay) skips it.
 *
 * Skipped `move`s snap the actor to the target AND face it along the dominant axis (IActor.faceTowards),
 * which is what a completed walk ends with in the common case. When the final facing matters, content
 * timelines put an explicit `face` after the last `move` (a grid path can end on its last segment).
 *
 * `play` (nested timeline) runs on a child player sharing this host: it registers its own skip handler
 * and `input:cancel` listener (its own skip scope — Esc skips the child, never the parent), inherits
 * skip mode from the parent (an already-skipped parent fast-forwards the child, state only) and
 * `step.skippable` overrides the child's own `skippable` when given.
 *
 * `abort()` is the external-stop path (scene shutdown while playing): no further step runs, waiters
 * are released, listeners/skip slot are unregistered and `play()` resolves 'skipped'. It is NOT a
 * skip — state is not replayed behind the player's back; callers that want convergence use `skip()`.
 */
import type { BattleOutcome, IActor, ICinematicPlayer, TimelineData, TimelineStep } from '@/core/types/contracts';
import type { WeatherState } from '@/core/types';
import { bus, Events } from '@/core/events';
import { getTimeline } from './registry';

export interface CinematicCamera {
  panTo(x: number, y: number, ms: number): Promise<void>;
  follow(): void;
  shake(ms: number, intensity: number): void;
  zoom?(zoom: number, ms: number): Promise<void>;
}

export interface CinematicHost {
  // ---- required (kept from the integrator stub)
  say(dialogueId: string): Promise<void>;
  setFlag(flag: string): void;
  setMark(mark: string): void;
  wait(ms: number): Promise<void>;
  // ---- optional capabilities
  hasMark?(mark: string): boolean;
  /** Apply a dialogue's marks/flags without showing it (used when skipping past `say`). */
  sayFastForward?(dialogueId: string): void;
  actor?(id: string): IActor | undefined;
  camera?: CinematicCamera;
  weather?(state: WeatherState, ms: number): void;
  fx?(effect: string, params?: Record<string, unknown>): Promise<void>;
  sfx?(key: string): void;
  music?(key: string | null, fadeMs: number): void;
  fade?(out: boolean, ms: number, color?: string): Promise<void>;
  letter?(paragraph: string, opts: { instant: boolean }): Promise<void>;
  launchMemory?(stage: string, opts: { fastForward: boolean }): Promise<'completed' | 'skipped'>;
  battle?(config: string): Promise<BattleOutcome>;
  setInputLocked?(locked: boolean): void;
  /** Registers/unregisters (null) the skip handler with the game context. */
  registerSkip?(fn: (() => boolean) | null): void;
  /** Called once when a skip is accepted (e.g. abort the running dialogue). */
  onSkip?(): void;
}

export interface CinematicPlayerOptions {
  lookup?: (id: string) => TimelineData | undefined;
  /** Listen for the abstract `input:cancel` bus event while playing (default true). */
  skipOnCancelEvent?: boolean;
  maxGotos?: number;
  /** Call host.setInputLocked(true/false) around play (default true; nested players leave it to the parent). */
  lockInput?: boolean;
  /** Nesting depth of a `play` child (internal); nesting deeper than MAX_NESTING is refused. */
  depth?: number;
}

/** Deepest allowed `play` nesting (parent → child → …); protects against self-referencing timelines. */
export const MAX_NESTING = 8;

export interface PlayOptions {
  /** Run the whole timeline in skip mode (state only, battles skipped). Result is 'skipped'. */
  fastForward?: boolean;
}

export type CinematicResult = 'completed' | 'skipped';

export class CinematicPlayer implements ICinematicPlayer {
  private playing = false;
  private skipped = false;
  private fastForwarding = false;
  private aborted = false;
  private current: TimelineData | null = null;
  private readonly skipWaiters = new Set<() => void>();
  private readonly pendingMoves = new Map<IActor, { x: number; y: number }>();
  private readonly lookup: (id: string) => TimelineData | undefined;
  private readonly skipOnCancel: boolean;
  private readonly maxGotos: number;
  private readonly lockInput: boolean;
  private readonly depth: number;
  /** >0 while a memory stage / battle sub-scene owns the input: `input:cancel` then belongs to it. */
  private subScenes = 0;
  /** Child player of a running `play` step (its own skip scope). */
  private nested: CinematicPlayer | null = null;
  /** True while our skip handler / cancel listener are registered (unregistered exactly once). */
  private registered = false;
  private readonly onCancelEvent = () => { if (this.subScenes === 0) this.skip(); };

  constructor(private readonly host: CinematicHost, opts: CinematicPlayerOptions = {}) {
    this.lookup = opts.lookup ?? getTimeline;
    this.skipOnCancel = opts.skipOnCancelEvent ?? true;
    this.maxGotos = opts.maxGotos ?? 100;
    this.lockInput = opts.lockInput ?? true;
    this.depth = opts.depth ?? 0;
  }

  isPlaying(): boolean { return this.playing; }
  wasSkipped(): boolean { return this.skipped; }
  wasAborted(): boolean { return this.aborted; }
  currentTimeline(): string | null { return this.current?.id ?? null; }
  /** The child player of a running `play` step, if any (its own skip scope). */
  nestedPlayer(): CinematicPlayer | null { return this.nested; }

  /** Returns true when a skip was accepted (or already in effect). */
  skip(): boolean {
    if (!this.playing || !this.current) return false;
    if (!this.current.skippable) return false;
    if (this.skipped) return true;
    this.skipped = true;
    bus.emitTyped(Events.CINEMATIC_SKIPPED, { id: this.current.id });
    try { this.host.onSkip?.(); } catch (e) { console.error('[cinematic] onSkip threw', e); }
    // A running nested timeline is part of this cutscene: cut it too (it refuses when non-skippable
    // and then finishes on its own, like a battle).
    this.nested?.skip();
    this.releaseSkipWaiters();
    return true;
  }

  /**
   * External stop (the owning scene is shutting down): stop running steps, release every waiter,
   * unregister the skip slot and cancel listener now (not when the scene's promises settle) and let
   * `play()` resolve 'skipped'. No-op when idle. Unlike `skip()` no state is replayed.
   */
  abort(): void {
    if (!this.playing || this.aborted) return;
    this.aborted = true;
    this.skipped = true;
    this.fastForwarding = true;
    this.nested?.abort();
    this.unregister();
    try { this.host.onSkip?.(); } catch (e) { console.error('[cinematic] onSkip threw', e); }
    this.releaseSkipWaiters();
  }

  private unregister(): void {
    if (!this.registered) return;
    this.registered = false;
    if (this.skipOnCancel) bus.offTyped(Events.INPUT_CANCEL, this.onCancelEvent);
    try { this.host.registerSkip?.(null); } catch (e) { console.warn('[cinematic] registerSkip(null) failed', e); }
  }

  async play(timeline: TimelineData | string, opts: PlayOptions = {}): Promise<CinematicResult> {
    const data = typeof timeline === 'string' ? this.lookup(timeline) : timeline;
    if (!data) { console.warn(`[cinematic] missing timeline "${String(timeline)}" — treated as completed`); return 'completed'; }
    if (this.playing) { console.warn(`[cinematic] "${data.id}" requested while "${this.current?.id}" is playing — ignored`); return 'completed'; }
    this.playing = true;
    this.skipped = !!opts.fastForward;
    this.fastForwarding = !!opts.fastForward;
    this.aborted = false;
    this.current = data;
    this.nested = null;
    this.pendingMoves.clear();
    bus.emitTyped(Events.CINEMATIC_START, { id: data.id });
    if (this.lockInput) this.host.setInputLocked?.(true);
    this.registered = true;
    this.host.registerSkip?.(() => this.skip());
    if (this.skipOnCancel) bus.onTyped(Events.INPUT_CANCEL, this.onCancelEvent);
    try {
      await this.runSteps(data.steps);
    } catch (e) {
      console.error(`[cinematic] error in "${data.id}"`, e);
    } finally {
      this.unregister();
      if (this.lockInput) this.host.setInputLocked?.(false);
      if (!this.aborted) this.snapPendingMoves(); else this.pendingMoves.clear();
      const result: CinematicResult = this.skipped ? 'skipped' : 'completed';
      this.playing = false;
      this.fastForwarding = false;
      this.current = null;
      this.nested = null;
      this.releaseSkipWaiters();
      bus.emitTyped(Events.CINEMATIC_END, { id: data.id });
      return result;
    }
  }

  // ------------------------------------------------------------------ execution

  private async runSteps(steps: TimelineStep[]): Promise<void> {
    const labels = new Map<string, number>();
    steps.forEach((s, i) => { if (s.op === 'label') labels.set(s.id, i); });
    let gotos = 0;
    for (let i = 0; i < steps.length; i++) {
      if (this.aborted) return;
      const step = steps[i] as TimelineStep;
      if (step.op === 'goto') {
        if (step.ifMark !== undefined) {
          if (!this.host.hasMark) { console.warn('[cinematic] goto.ifMark needs host.hasMark — not jumping'); continue; }
          if (!this.host.hasMark(step.ifMark)) continue;
        }
        const target = labels.get(step.label);
        if (target === undefined) { console.warn(`[cinematic] goto: unknown label "${step.label}"`); continue; }
        if (++gotos > this.maxGotos) { console.warn(`[cinematic] more than ${this.maxGotos} gotos — stopping timeline`); break; }
        i = target; // loop increment moves past the label
        continue;
      }
      await this.exec(step);
    }
  }

  private async exec(step: TimelineStep): Promise<void> {
    if (this.aborted) return;
    if (this.skipped) { await this.fast(step); return; }
    const host = this.host;
    switch (step.op) {
      case 'move': {
        const a = this.actor(step.actor); if (!a) return;
        const target = { x: Math.round(step.x), y: Math.round(step.y) };
        this.pendingMoves.set(a, target);
        const p = a.walkTo(target.x, target.y, step.speed).catch((e) => console.warn('[cinematic] walkTo failed', e)).then(() => { if (this.pendingMoves.get(a) === target) this.pendingMoves.delete(a); });
        if (step.wait === false) return;
        await this.untilSkip(p);
        if (this.skipped) this.snap(a, target);
        return;
      }
      case 'face': this.actor(step.actor)?.setFacing(step.facing); return;
      case 'anim': this.actor(step.actor)?.play(step.state, step.emote); return;
      case 'emote': {
        const a = this.actor(step.actor); if (!a) return;
        await this.untilSkip(a.showEmote(step.emote, step.ms));
        return;
      }
      case 'say':
        await this.untilSkip(host.say(step.dialogue));
        if (this.skipped) host.sayFastForward?.(step.dialogue);
        return;
      case 'camera': {
        const cam = host.camera;
        if (!cam) { this.missing('camera'); return; }
        if (step.shake) cam.shake(step.shake.ms, step.shake.intensity);
        if (step.zoom !== undefined && cam.zoom) await this.untilSkip(cam.zoom(step.zoom, step.ms ?? 0));
        if (step.x !== undefined && step.y !== undefined) await this.untilSkip(cam.panTo(step.x, step.y, step.ms ?? 0));
        if (this.skipped) await this.fast(step);
        else if (step.follow) cam.follow();
        return;
      }
      case 'wait': await this.untilSkip(host.wait(step.ms)); return;
      case 'weather': if (host.weather) host.weather(step.state, step.ms ?? 1000); else this.missing('weather'); return;
      case 'fx': {
        if (!host.fx) { this.missing('fx'); return; }
        const p = host.fx(step.effect, step.params).catch((e) => console.warn('[cinematic] fx failed', e));
        if (step.wait === false) return;
        await this.untilSkip(p);
        return;
      }
      case 'sfx': if (host.sfx) host.sfx(step.key); else this.missing('sfx'); return;
      case 'music': if (host.music) host.music(step.key, step.fadeMs ?? 800); else this.missing('music'); return;
      case 'fade': if (host.fade) await this.untilSkip(host.fade(step.out, step.ms, step.color)); else this.missing('fade'); return;
      case 'setFlag': host.setFlag(step.flag); return;
      case 'setMark': host.setMark(step.mark); return;
      case 'launchMemory':
        if (host.launchMemory) await this.sub(() => host.launchMemory!(step.stage, { fastForward: false })); else this.missing('launchMemory');
        return;
      case 'battle':
        if (host.battle) await this.sub(() => host.battle!(step.config)); else this.missing('battle');
        return;
      case 'letter':
        if (host.letter) await this.untilSkip(host.letter(step.paragraph, { instant: false })); else this.missing('letter');
        return;
      case 'parallel': await Promise.all(step.steps.map((s) => this.exec(s))); return;
      case 'play': await this.playNested(step, false); return;
      case 'label': return;
      case 'goto': console.warn('[cinematic] goto inside parallel is not supported'); return;
      default: this.unknown(step); return;
    }
  }

  /** Skip-mode execution: apply end state only; never waits on presentation, only on sub-scenes. */
  private async fast(step: TimelineStep): Promise<void> {
    if (this.aborted) return;
    const host = this.host;
    switch (step.op) {
      case 'move': { const a = this.actor(step.actor); if (a) this.snap(a, { x: Math.round(step.x), y: Math.round(step.y) }); return; }
      case 'face': this.actor(step.actor)?.setFacing(step.facing); return;
      case 'anim': this.actor(step.actor)?.play(step.state, step.emote); return;
      case 'say': host.sayFastForward?.(step.dialogue); return;
      case 'camera': {
        const cam = host.camera; if (!cam) return;
        if (step.zoom !== undefined && cam.zoom) void cam.zoom(step.zoom, 0);
        if (step.x !== undefined && step.y !== undefined) void cam.panTo(step.x, step.y, 0);
        if (step.follow) cam.follow();
        return;
      }
      case 'weather': host.weather?.(step.state, 0); return;
      case 'music': host.music?.(step.key, 0); return;
      case 'fade': void host.fade?.(step.out, 0, step.color)?.catch(() => { /* ignore */ }); return;
      case 'setFlag': host.setFlag(step.flag); return;
      case 'setMark': host.setMark(step.mark); return;
      case 'launchMemory': if (host.launchMemory) await this.sub(() => host.launchMemory!(step.stage, { fastForward: true })); return;
      case 'battle': if (!this.fastForwarding && host.battle) await this.sub(() => host.battle!(step.config)); return;
      case 'letter': if (host.letter) await this.settle(host.letter(step.paragraph, { instant: true })); return;
      case 'parallel': await Promise.all(step.steps.map((s) => this.fast(s))); return;
      case 'play': await this.playNested(step, true); return;
      case 'emote': case 'wait': case 'fx': case 'sfx': case 'label': case 'goto': return;
      default: this.unknown(step); return;
    }
  }

  /**
   * `play` op: runs the referenced timeline on a child player that shares this host. The child owns
   * Esc/`input:cancel` while it runs (own skip scope; the parent ignores cancels via `sub()`);
   * `fastForward` (parent already skipped) replays the child's state only.
   */
  private async playNested(step: Extract<TimelineStep, { op: 'play' }>, fastForward: boolean): Promise<void> {
    const data = this.lookup(step.timeline);
    if (!data) { console.warn(`[cinematic] play: missing timeline "${step.timeline}" — step ignored`); return; }
    if (this.depth + 1 > MAX_NESTING) { console.warn(`[cinematic] play: "${step.timeline}" nested deeper than ${MAX_NESTING} — step ignored`); return; }
    if (this.nested) { console.warn(`[cinematic] play: "${step.timeline}" requested while "${this.nested.currentTimeline()}" is nested (parallel play?) — step ignored`); return; }
    const timeline: TimelineData = step.skippable === undefined ? data : { ...data, skippable: step.skippable };
    const child = new CinematicPlayer(this.host, { lookup: this.lookup, skipOnCancelEvent: this.skipOnCancel, maxGotos: this.maxGotos, lockInput: false, depth: this.depth + 1 });
    this.nested = child;
    try { await this.sub(() => child.play(timeline, { fastForward })); }
    finally { if (this.nested === child) this.nested = null; }
  }

  private unknown(step: TimelineStep): void {
    console.warn(`[cinematic] unknown op "${String((step as { op?: unknown }).op)}" — step ignored`);
  }

  private actor(id: string): IActor | undefined {
    const a = this.host.actor?.(id);
    if (!a) {
      // Count as warning on global handle without spamming console on scene re-entry
      if (typeof window !== 'undefined' && window.__nantian) {
        window.__nantian.warnings.push(`[cinematic] actor "${id}" not found`);
      }
    }
    return a;
  }

  /** Skipped move: stop, face the target along the dominant axis (what a finished walk ends with), teleport. */
  private snap(a: IActor, to: { x: number; y: number }): void {
    try {
      a.stop();
      if (a.x !== to.x || a.y !== to.y) a.faceTowards(to.x, to.y);
      a.x = to.x; a.y = to.y;
    } catch (e) { console.warn('[cinematic] snap failed', e); }
    this.pendingMoves.delete(a);
  }

  private snapPendingMoves(): void {
    if (!this.skipped) { this.pendingMoves.clear(); return; }
    for (const [a, to] of [...this.pendingMoves]) this.snap(a, to);
  }

  private missing(cap: string): void {
    console.warn(`[cinematic] host has no "${cap}" capability — step ignored`);
  }

  /** Awaits a presentation promise fully (letter paragraph); failures are logged, never fatal. */
  private settle<T>(p: Promise<T>): Promise<T | undefined> {
    return p.then((v) => v, (e) => { console.warn('[cinematic] step failed', e); return undefined; });
  }

  /** Runs a sub-scene (memory stage / battle) to completion; its own player owns Esc meanwhile. */
  private async sub<T>(start: () => Promise<T>): Promise<T | undefined> {
    this.subScenes++;
    try { return await start(); }
    catch (e) { console.warn('[cinematic] sub-scene failed', e); return undefined; }
    finally { this.subScenes--; }
  }

  /** Resolves when `p` settles or when skip is triggered, whichever comes first. */
  private untilSkip<T>(p: Promise<T>): Promise<T | undefined> {
    if (this.skipped) return Promise.resolve(undefined);
    return new Promise<T | undefined>((resolve) => {
      const onSkip = () => resolve(undefined);
      this.skipWaiters.add(onSkip);
      p.then((v) => { this.skipWaiters.delete(onSkip); resolve(v); }, (e) => { this.skipWaiters.delete(onSkip); console.warn('[cinematic] step failed', e); resolve(undefined); });
    });
  }

  private releaseSkipWaiters(): void {
    for (const w of [...this.skipWaiters]) w();
    this.skipWaiters.clear();
  }
}
